
import React, { useState, useEffect } from "react";
import { getTemplateCategories, Template } from "@/data/templates";
import DocumentTemplatesHeader from "./DocumentTemplatesHeader";
import DocumentTemplatesLayout from "./DocumentTemplatesLayout";
import CategoriesView from "./CategoriesView";
import SubcategoriesView from "./SubcategoriesView";
import TemplatesView from "./TemplatesView";

interface DocumentTemplatesProps {
  onSelectTemplate: (template: Template) => void;
  onBack: () => void;
}

const DocumentTemplates = ({ onSelectTemplate, onBack }: DocumentTemplatesProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  
  // ✅ Loading/Error states para evitar tela branca
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  // ✅ Carregar templates com try/catch
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cats = await Promise.resolve(getTemplateCategories());
        if (!mounted) return;
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || "Falha ao carregar templates");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Encontrar categoria e subcategoria selecionadas
  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const selectedSubcategoryData = selectedCategoryData?.subcategories.find(s => s.id === selectedSubcategory);

  // Handlers para navegação
  const handleSelectCategory = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setGlobalSearchTerm("");
  };

  const handleSelectSubcategory = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);
    setSearchTerm("");
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm("");
    setGlobalSearchTerm("");
  };

  const handleBackToSubcategories = () => {
    setSelectedSubcategory(null);
    setSearchTerm("");
  };

  // Renderizar conteúdo baseado no estado atual
  const renderContent = () => {
    if (!selectedCategory) {
      return (
        <CategoriesView
          categories={categories}
          globalSearchTerm={globalSearchTerm}
          setGlobalSearchTerm={setGlobalSearchTerm}
          onSelectCategory={handleSelectCategory}
          onSelectTemplate={onSelectTemplate}
        />
      );
    }

    if (selectedCategory && !selectedSubcategory && selectedCategoryData) {
      return (
        <SubcategoriesView
          category={selectedCategoryData}
          onBack={handleBackToCategories}
          onSelectSubcategory={handleSelectSubcategory}
        />
      );
    }

    if (selectedCategory && selectedSubcategory && selectedCategoryData && selectedSubcategoryData) {
      return (
        <TemplatesView
          category={selectedCategoryData}
          subcategory={selectedSubcategoryData}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onBack={handleBackToSubcategories}
          onSelectTemplate={onSelectTemplate}
        />
      );
    }

    return null;
  };

  // ✅ Fallbacks para loading/error/vazio
  if (loading) return (
    <DocumentTemplatesLayout header={<DocumentTemplatesHeader onBack={onBack} />}>
      <div className="text-sm text-muted-foreground p-4">Carregando modelos…</div>
    </DocumentTemplatesLayout>
  );
  
  if (error) return (
    <DocumentTemplatesLayout header={<DocumentTemplatesHeader onBack={onBack} />}>
      <div className="text-sm text-destructive p-4">Erro: {error}</div>
    </DocumentTemplatesLayout>
  );
  
  if (!categories.length) return (
    <DocumentTemplatesLayout header={<DocumentTemplatesHeader onBack={onBack} />}>
      <div className="text-sm text-muted-foreground p-4">Nenhum modelo disponível no momento.</div>
    </DocumentTemplatesLayout>
  );

  return (
    <DocumentTemplatesLayout
      header={<DocumentTemplatesHeader onBack={onBack} />}
    >
      {renderContent()}
    </DocumentTemplatesLayout>
  );
};

export default DocumentTemplates;
